import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PasswordField from '../../components/PasswordField/PasswordField';
import AccountSessionError from './AccountSessionError';
import {
  clearStoredAuthIfUnauthorized,
  readStoredAuth,
  SIGN_IN_REQUIRED_MESSAGE,
} from './profileFormUtils';
import {
  apiChangePassword,
  changePasswordErrorMessage as mapChangePasswordApiError,
  validateNewPasswordForApi,
} from './authRecoveryApi';
import './AccountLayout.css';

const DOC_TITLE = 'Change password · Digi Rasa';

const AccountChangePassword = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const prev = document.title;
    document.title = DOC_TITLE;
    return () => {
      document.title = prev;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword) {
      setError('Enter your current password.');
      return;
    }
    const pwMsg = validateNewPasswordForApi(newPassword);
    if (pwMsg) {
      setError(pwMsg);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from your current password.');
      return;
    }

    const { token } = readStoredAuth();
    if (!token) {
      setError(SIGN_IN_REQUIRED_MESSAGE);
      return;
    }

    setSaving(true);
    try {
      const { res, data } = await apiChangePassword(token, {
        oldPassword: currentPassword,
        newPassword,
      });
      if (!res.ok) {
        clearStoredAuthIfUnauthorized(res.status);
        throw new Error(mapChangePasswordApiError(res.status, data));
      }
      let successMsg =
        'Your password was changed successfully. Please sign in with your new password.';
      if (data && typeof data.message === 'string' && data.message.trim()) {
        successMsg = data.message.trim();
      }
      // Navigate first while session still exists so AccountLayout does not
      // redirect to login before the success page can render. Logout runs on
      // the public /account/password-updated page.
      try {
        sessionStorage.setItem(
          'digirasa_password_updated',
          JSON.stringify({
            title: 'Password changed',
            message: successMsg,
            signedOut: true,
          }),
        );
      } catch {
        /* ignore */
      }
      navigate('/account/password-updated', {
        replace: true,
        state: {
          title: 'Password changed',
          message: successMsg,
          signedOut: true,
        },
      });
    } catch (err) {
      setError(err.message || 'Could not change password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="account-page">
      <div className="account-page__title-block">
        <h1>Change password</h1>
        <p className="account-page__muted">
          Update the password for your DigiRasa account. You&rsquo;ll need your current password to
          confirm this change.
        </p>
      </div>

      {error ? (
        <AccountSessionError
          message={error}
          returnPath="/account/change-password"
          signInSuffix="to continue"
          variant="inline"
        />
      ) : null}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="auth-form__group">
          <label htmlFor="cp-current">Current password</label>
          <PasswordField
            id="cp-current"
            value={currentPassword}
            onChange={(ev) => setCurrentPassword(ev.target.value)}
            placeholder="Enter your current password"
            autoComplete="current-password"
            disabled={saving}
          />
        </div>

        <div className="auth-form__group">
          <label htmlFor="cp-new">New password</label>
          <PasswordField
            id="cp-new"
            value={newPassword}
            onChange={(ev) => setNewPassword(ev.target.value)}
            placeholder="8+ chars, letters, numbers, special character"
            autoComplete="new-password"
            disabled={saving}
          />
          <p className="auth-form__hint">
            No spaces. Include letters, numbers, and a special character.
          </p>
        </div>

        <div className="auth-form__group">
          <label htmlFor="cp-confirm">Confirm new password</label>
          <PasswordField
            id="cp-confirm"
            value={confirmPassword}
            onChange={(ev) => setConfirmPassword(ev.target.value)}
            placeholder="Re-enter your new password"
            autoComplete="new-password"
            disabled={saving}
          />
        </div>

        <button type="submit" className="auth-form__submit" disabled={saving}>
          {saving ? 'Saving…' : 'Change password'}
        </button>
      </form>

      <p className="account-page__muted" style={{ marginTop: '16px' }}>
        Forgot your current password?{' '}
        <Link to="/account/reset-password">Reset it with a one-time code</Link>.
      </p>
    </div>
  );
};

export default AccountChangePassword;
